import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import {
  getAssessmentContentBlank,
  getAssessmentContentCutoffs,
  getAssessmentText,
  getLeftBarColorClassCutoffs,
  getRightBarColorClassCutoffs,
} from '../report-util';
import { useAuth } from '../../../../providers/auth-provider';
import {
  getCompetenciesBreakpoints,
  getCompetenciesSummaryText,
  getCompetenciesSummary,
  getCompetenciesTitle,
  getInterviewBreakpoints,
  getInterviewSummaryText,
  getInterviewSummary,
  getInterviewTitle,
  getInterviewOverallBreakpoints,
} from '../assessment-verbiage';
import { REPORT_TYPE } from '../../../../utils/constants';

const ratingOverallBreakpoints = {
  breaks: [4.25, 3.0],
  scale: 5,
};

function CompetenciesDetail({ candidate, reportType = REPORT_TYPE.development }) {
  const { isDevelop } = useAuth();
  const [heading, setHeading] = useState('Overall Interview');

  const getCompetencyScore = useCallback(
    (key) => {
      if (candidate.raterCompetencies?.scores) {
        return candidate.raterCompetencies.scores[key];
      }
      return candidate.selfCompetencies.scores[key];
    },
    [candidate]
  );

  const isDevelopWithRaters = useCallback(() => {
    return isDevelop() && candidate.raterCompetencies?.scores;
  }, [candidate, isDevelop]);

  const getInterviewOverallText = useCallback(() => {
    const overallScore = candidate.interviewCompetencies.scores.overall;

    if (overallScore >= 27) {
      return 'Highly Recommend';
    } else if (overallScore >= 23) {
      return 'Recommend';
    } else if (overallScore >= 19) {
      return 'Minimally Recommend';
    }

    return 'Do Not Recommend';
  }, [candidate]);

  const getOverallBar = (title, value, breakpoints, valueText = undefined) => {
    if (value === undefined) {
      return <></>;
    }

    const breaks = breakpoints.breaks;
    const scale = breakpoints.scale;

    const divisor = 100 / scale;

    return (
      <Stack>
        <div className="text-larger text-bold pt-3">{title}</div>
        <Stack className="chart-data" direction="horizontal">
          <>
            <div className="w-25">
              <div className="progress-chart">
                <div
                  className={`chart-bar chart-bar-left ${getLeftBarColorClassCutoffs(value, breaks, reportType)}`}
                  style={{ width: `${value * divisor}%` }}
                />
                <div
                  className={`chart-bar chart-bar-right ${getRightBarColorClassCutoffs(value, breaks, reportType)}`}
                  style={{ width: `${100 - value * divisor}%` }}
                />
              </div>
            </div>
            <div className="d-flex w-75 h-100 ps-4 align-items-center">
              {valueText ? (
                <div className="text-bolder">{valueText}</div>
              ) : (
                <div className="text-bolder">{`${value.toFixed(1)}`}</div>
              )}
            </div>
          </>
        </Stack>
      </Stack>
    );
  };

  useEffect(() => {
    if (candidate) {
      if (isDevelop()) {
        setHeading('Overall Leadership');
      }
    }
  }, [candidate]);

  return (
    <Container className="candidate-profile-assessment-content">
      <Row className="w-100 justify-content-left">
        <Stack direction="horizontal" gap={4}>
          {isDevelop()
            ? getOverallBar(heading, candidate.selfCompetencies.scores.overallLeadership, ratingOverallBreakpoints)
            : getOverallBar(
                heading,
                candidate.interviewCompetencies.scores.overall,
                getInterviewOverallBreakpoints(candidate),
                getInterviewOverallText()
              )}
        </Stack>
      </Row>
      <Row className="w-100 pb-3">
        {isDevelop() && isDevelopWithRaters() && getAssessmentText(getCompetenciesSummaryText(candidate))}
        {!isDevelop() && getAssessmentText(getInterviewSummaryText(candidate))}
      </Row>
      {isDevelopWithRaters() && (
        <Row className="w-100 pb-3">
          <div>
            The information below represents the individual&apos;s performance on the key competencies from the
            perspective of their raters.
          </div>
        </Row>
      )}
      <Row className="w-100 mx-auto justify-content-center pt-2">
        {isDevelop() ? (
          <>
            {candidate.selfCompetencies.variableKeys.map((key) => {
              return (
                <Stack key={key} direction="horizontal" className="subscore-chart">
                  <div className="w-25">
                    {getAssessmentContentCutoffs(
                      getCompetenciesTitle(candidate, key),
                      getCompetencyScore(key),
                      getCompetenciesBreakpoints(candidate),
                      reportType
                    )}
                  </div>
                  <div className="w-75">{getCompetenciesSummary(candidate, key)}</div>
                </Stack>
              );
            })}
          </>
        ) : (
          <>
            {candidate.interviewCompetencies.variableKeys.map((key) => {
              return (
                <Stack key={key} direction="horizontal" className="subscore-chart">
                  <div className="w-25">
                    {getAssessmentContentBlank(
                      getInterviewTitle(candidate, key),
                      candidate.interviewCompetencies.scores[key],
                      getInterviewBreakpoints(),
                      reportType
                    )}
                  </div>
                  <div className="w-75">{getInterviewSummary(candidate, key)}</div>
                </Stack>
              );
            })}
          </>
        )}
      </Row>
    </Container>
  );
}

export default CompetenciesDetail;
